import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { HACKATHON } from "@/data/agenticHackathon";
import { springTransition } from "@/lib/motionVariants";

function getCalendarParts(iso) {
    const d = new Date(`${iso}T12:00:00`);
    return {
        month: d.toLocaleString("en-US", { month: "short" }).toUpperCase(),
        day: d.getDate(),
        year: d.getFullYear(),
    };
}

function useCountdown(targetIso) {
    const [left, setLeft] = useState(null);

    useEffect(() => {
        const target = new Date(`${targetIso}T08:00:00`).getTime();

        const tick = () => {
            const diff = target - Date.now();
            if (diff <= 0) {
                setLeft({ done: true });
                return;
            }
            setLeft({
                done: false,
                days: Math.floor(diff / 86400000),
                hours: Math.floor((diff % 86400000) / 3600000),
                minutes: Math.floor((diff % 3600000) / 60000),
                seconds: Math.floor((diff % 60000) / 1000),
            });
        };

        tick();
        const id = window.setInterval(tick, 1000);
        return () => window.clearInterval(id);
    }, [targetIso]);

    return left;
}

function AnimatedDay({ target, reduceMotion }) {
    const [value, setValue] = useState(reduceMotion ? target : 0);

    useEffect(() => {
        if (reduceMotion) {
            setValue(target);
            return undefined;
        }
        let current = 0;
        const step = () => {
            current += 1;
            setValue(current);
            if (current < target) {
                window.setTimeout(step, 70);
            }
        };
        const start = window.setTimeout(step, 400);
        return () => window.clearTimeout(start);
    }, [target, reduceMotion]);

    return (
        <motion.span
            key={value}
            initial={reduceMotion ? false : { opacity: 0, y: 12, scale: 0.85 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={springTransition}
            className="font-display font-light text-zinc-900 leading-none tabular-nums text-[clamp(2rem,42cqh,4.5rem)]"
        >
            {value}
        </motion.span>
    );
}

export function HackathonCountdown({ reduceMotion: reduceMotionProp }) {
    const systemReduce = useReducedMotion();
    const reduceMotion = reduceMotionProp ?? systemReduce;
    const left = useCountdown(HACKATHON.dateIso);

    if (!left) {
        return (
            <p className="mt-3 text-xs sm:text-sm font-mono text-white/50 animate-pulse">
                Loading countdown…
            </p>
        );
    }

    if (left.done) {
        return (
            <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-3 text-xs sm:text-sm font-mono text-emerald-400/90"
            >
                {HACKATHON.dateLabel} — see you there
            </motion.p>
        );
    }

    const units = [
        { v: left.days, label: "d" },
        { v: left.hours, label: "h" },
        { v: left.minutes, label: "m" },
        { v: left.seconds, label: "s" },
    ];

    return (
        <motion.p
            initial={reduceMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, ...springTransition }}
            className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs sm:text-sm font-mono text-white/60"
            aria-live="polite"
        >
            <span className="text-white/45">Countdown ·</span>
            {units.map(({ v, label }) => (
                <span key={label} className="inline-flex items-baseline gap-0.5">
                    <motion.span
                        key={`${label}-${v}`}
                        initial={reduceMotion ? false : { opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-[#E54D2E] font-semibold tabular-nums"
                    >
                        {v}
                    </motion.span>
                    <span className="text-white/50">{label}</span>
                </span>
            ))}
            <span className="text-white/45 w-full sm:w-auto">to Agentic Hack</span>
        </motion.p>
    );
}

/**
 * macOS Calendar tile — stretches to match title height, animated date.
 */
export default function HackathonCalendarDate({ className = "" }) {
    const reduceMotion = useReducedMotion();
    const { month, day, year } = getCalendarParts(HACKATHON.dateIso);

    return (
        <motion.div
            data-testid="hackathon-calendar-date"
            initial={reduceMotion ? false : { opacity: 0, scale: 0.92, x: -12 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={springTransition}
            className={`@container flex h-full w-auto aspect-[5/7] min-h-[calc(1.85rem*1.76)] sm:min-h-[calc(3rem*1.76)] md:min-h-[calc(3.75rem*1.76)] lg:min-h-[calc(4.5rem*1.76)] shrink-0 flex-col overflow-hidden rounded-[10px] sm:rounded-xl bg-white shadow-[0_10px_32px_-8px_rgba(0,0,0,0.45)] ring-1 ring-black/10 ${className}`}
            aria-label={HACKATHON.dateLabel}
        >
                <motion.div
                    className="bg-[#E54D2E] text-white text-center px-2 py-1.5 sm:py-2 md:py-2.5 shrink-0"
                    animate={
                        reduceMotion
                            ? undefined
                            : {
                                  boxShadow: [
                                      "0 0 0 rgba(229,77,46,0)",
                                      "0 0 14px rgba(229,77,46,0.45)",
                                      "0 0 0 rgba(229,77,46,0)",
                                  ],
                              }
                    }
                    transition={{
                        duration: 2.2,
                        repeat: Infinity,
                        ease: "easeInOut",
                    }}
                >
                    <motion.span
                        initial={reduceMotion ? false : { opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.15, ...springTransition }}
                        className="text-[10px] sm:text-xs md:text-sm font-semibold tracking-[0.16em] uppercase"
                    >
                        {month}
                    </motion.span>
                </motion.div>
                <div className="flex flex-1 flex-col items-center justify-center bg-gradient-to-b from-white to-zinc-50 px-2 py-1 sm:py-2 min-h-0">
                    <AnimatedDay target={day} reduceMotion={reduceMotion} />
                    <motion.span
                        initial={reduceMotion ? false : { opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.65, ...springTransition }}
                        className="text-[10px] sm:text-xs md:text-sm font-medium text-zinc-500 tabular-nums mt-0.5 sm:mt-1"
                    >
                        {year}
                    </motion.span>
            </div>
        </motion.div>
    );
}
